// Saved Linux machines ("profiles"): validation, defaults, and a localStorage-backed store whose
// writes are serialised across tabs. Ported from EndisOS src/linux/profiles.ts. The list lives
// in revault:linux-machines, so it is included in Re:Vault backups; disks/snapshots are not.
//
// A machine:
//   { id, name, engine: "v86" | "cheerpx", createdAt, image, persist,
//     memory (v86 MiB), network (v86: guest networking through Re:Vault's wisp), autologin (v86), acpi (v86),
//     cmd, args, env, cwd, uid, gid (CheerpX process), desktop (CheerpX graphical session) }
// image: "catalog:<id>" (served by Re:Vault), "upload:<id>" (IndexedDB), "" (none, v86 only),
//        or a wss:// / https:// disk URL (CheerpX).
import { readJSON, writeJSON } from "../shared/store.js";
import { MACHINES_KEY, ACTIVE_KEY } from "../shared/linux-store.js";
import { LINUX_IMAGES, CHEERPX_IMAGES, CHEERPX_DEBIAN_IMAGE, CHEERPX_DESKTOP_IMAGE } from "../shared/linux-catalog.js";
import { withMutationLock, MACHINE_ID_RE } from "./lock.js";
import { LinuxError } from "./engine.js";

export const MAX_MACHINES = 32;
export const MIN_MEMORY = 32;
export const MAX_MEMORY = 2048;
export const DEFAULT_MEMORY = 256;

const isStr = (v, max = 4096) => typeof v === "string" && v.length <= max;
const isStrArray = (v) => Array.isArray(v) && v.length <= 64 && v.every((x) => isStr(x));
const isUint = (v) => typeof v === "number" && Number.isInteger(v) && v >= 0 && v < 2 ** 31;

export function isImageRef(engine, image) {
	if (!isStr(image)) return false;
	if (engine === "v86") {
		if (image === "") return true;
		const m = /^(catalog|upload):([a-z0-9][a-z0-9.-]{0,79})$/.exec(image);
		return !!m && (m[1] === "upload" || LINUX_IMAGES.some((i) => i.id === m[2]));
	}
	// CheerpX: a disk URL (wss:// = Leaning's CloudDevice, https:// = HttpBytesDevice with Range + CORS),
	// or "rv:<id>" for a disk Re:Vault serves itself (resolved against the current origin at start).
	if (image.startsWith("rv:")) return CHEERPX_IMAGES.some((i) => i.id === image.slice(3));
	try { const u = new URL(image); return (u.protocol === "wss:" || u.protocol === "https:") && !u.username && !u.password; }
	catch (e) { return false; }
}

// Never trust stored shapes: the key is shared by every tab and restored from backups.
export function isMachine(m) {
	if (!m || typeof m !== "object") return false;
	if (!isStr(m.id) || !MACHINE_ID_RE.test(m.id)) return false;
	if (!isStr(m.name, 80) || !m.name.trim()) return false;
	if (m.engine !== "v86" && m.engine !== "cheerpx") return false;
	if (typeof m.createdAt !== "number" || !Number.isFinite(m.createdAt)) return false;
	if (!isImageRef(m.engine, m.image) || typeof m.persist !== "boolean") return false;
	if (m.engine === "v86" && !(isUint(m.memory) && m.memory >= MIN_MEMORY && m.memory <= MAX_MEMORY)) return false;
	if (m.network !== undefined && typeof m.network !== "boolean") return false;
	if (m.desktop !== undefined && typeof m.desktop !== "boolean") return false;
	if (m.autologin !== undefined && typeof m.autologin !== "boolean") return false;
	if (m.acpi !== undefined && typeof m.acpi !== "boolean") return false;
	if (m.engine === "cheerpx") {
		if (!isStr(m.cmd) || !m.cmd.startsWith("/") || !isStrArray(m.args) || !isStrArray(m.env) || !isStr(m.cwd) || !isUint(m.uid) || !isUint(m.gid)) return false;
	}
	return true;
}

export function slugify(name, taken) {
	const base = String(name || "").toLowerCase().normalize("NFKD").replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 48) || "machine";
	let id = base, n = 2;
	while (taken.has(id)) id = base + "-" + n++;
	return id;
}

export function v86Machine(fields) {
	return {
		id: fields.id || "alpine", name: fields.name || "Alpine Linux", engine: "v86", createdAt: fields.createdAt || Date.now(),
		image: fields.image !== undefined ? fields.image : "catalog:" + LINUX_IMAGES[0].id,
		persist: fields.persist !== undefined ? !!fields.persist : true,
		memory: fields.memory || DEFAULT_MEMORY, network: fields.network !== undefined ? !!fields.network : true,
		// Alpine's live ISO: log in as root, bring up the network and mount the file share on a fresh boot.
		autologin: fields.autologin !== undefined ? !!fields.autologin : true,
		// ACPI power management. Windows Vista and later refuse to boot without it ("BIOS is not ACPI
		// compatible"); off by default so existing machines and their saved states stay as they were.
		acpi: fields.acpi !== undefined ? !!fields.acpi : false,
	};
}
// Process options mirror WebVM's public terminal config (EndisOS verified them against 1.3.9).
export function debianMachine(fields = {}) {
	return {
		id: fields.id || "debian", name: fields.name || "Debian (terminal)", engine: "cheerpx", createdAt: fields.createdAt || Date.now(),
		image: CHEERPX_DEBIAN_IMAGE, persist: true, desktop: false,
		cmd: "/bin/bash", args: ["--login"],
		env: ["HOME=/home/user", "TERM=xterm", "USER=user", "SHELL=/bin/bash", "EDITOR=vim", "LANG=en_US.UTF-8", "LC_ALL=C"],
		cwd: "/home/user", uid: 1000, gid: 1000,
	};
}
export function desktopMachine(fields = {}) {
	return {
		id: fields.id || "alpine-desktop", name: fields.name || "Alpine desktop", engine: "cheerpx", createdAt: fields.createdAt || Date.now(),
		image: CHEERPX_DESKTOP_IMAGE, persist: true, desktop: true,
		cmd: "/sbin/init", args: [], env: ["TERM=xterm", "HOME=/root", "USER=root"], cwd: "/", uid: 0, gid: 0,
	};
}
// Debian 12 with an LXDE desktop, built and served by Re:Vault (tools/debian-desktop). Its process,
// revault-desktop, brings up the system bus, Xorg on the CheerpX KMS device, the LXDE session as "user", and a root
// shell on the Console tab.
export const DEBIAN_DESKTOP_ID = "debian-desktop-12";
export function debianDesktopMachine(fields = {}) {
	return {
		id: fields.id || "debian-desktop", name: fields.name || "Debian desktop", engine: "cheerpx", createdAt: fields.createdAt || Date.now(),
		image: "rv:" + DEBIAN_DESKTOP_ID, persist: true, desktop: true,
		cmd: "/usr/local/sbin/revault-desktop", args: [], env: ["TERM=xterm", "HOME=/root", "USER=root", "LANG=C.UTF-8"], cwd: "/root", uid: 0, gid: 0,
	};
}
export const debianDesktopAvailable = () => CHEERPX_IMAGES.some((i) => i.id === DEBIAN_DESKTOP_ID);
export function defaultMachines(memory = DEFAULT_MEMORY) {
	const now = Date.now();
	return [v86Machine({ memory, createdAt: now }), debianMachine({ createdAt: now + 1 }), desktopMachine({ createdAt: now + 2 })];
}

// Sync read (localStorage) so the page can render its machine list on first paint.
export function listMachines() {
	const raw = readJSON(MACHINES_KEY, null);
	return Array.isArray(raw) ? raw.filter(isMachine).slice(0, MAX_MACHINES) : [];
}
export const getMachine = (id) => listMachines().find((m) => m.id === id) || null;

// First visit: seed the default machines once. An explicitly emptied list stays empty.
export function ensureDefaults(memory) {
	return withMutationLock("machines", () => {
		if (readJSON(MACHINES_KEY, null) === null) writeJSON(MACHINES_KEY, defaultMachines(memory));
		return listMachines();
	});
}

// Insert or replace by id. Commits to storage first; callers re-render from storage afterwards.
export function saveMachine(machine) {
	return withMutationLock("machines", () => {
		if (!isMachine(machine)) throw new LinuxError("PROCESS_FAILED", "That machine's settings aren't valid.");
		const list = listMachines();
		const i = list.findIndex((m) => m.id === machine.id);
		if (i >= 0) list[i] = machine;
		else {
			// Enforce the limit before committing and say so - never silently drop the new machine.
			if (list.length >= MAX_MACHINES) throw new LinuxError("STORAGE_FAILED", "You can save up to " + MAX_MACHINES + " Linux machines. Delete one you don't use first.");
			list.push(machine);
		}
		writeJSON(MACHINES_KEY, list);
		return machine;
	});
}

export function removeMachine(id) {
	return withMutationLock("machines", () => {
		writeJSON(MACHINES_KEY, listMachines().filter((m) => m.id !== id));
		if (readJSON(ACTIVE_KEY, null) === id) writeJSON(ACTIVE_KEY, null);
	});
}

export const getActiveId = () => { const v = readJSON(ACTIVE_KEY, null); return typeof v === "string" ? v : null; };
export const setActiveId = (id) => writeJSON(ACTIVE_KEY, id || null);
